import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import {
  SwitcherCaret,
  WorkspaceRowIcon,
} from "@/pages/workspaces/home/WorkspaceSwitcher";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import type { MetadataIcon } from "@cohesive/api-client";
import { PanelsTopLeft } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { setFullAppOverride } from "./fullAppOverride";
import { MobileWorkspaceSwitcherSheet } from "./MobileWorkspaceSwitcherSheet";

/**
 * Mobile stand-in for `HomeGreeting` / `WorkspaceSurfaceHeader`, passed as
 * `HomeSurface`'s `title`. Same trigger contract as desktop — tap the
 * greeting (unscoped) or the workspace title (scoped) to switch workspace —
 * but the switcher opens as a bottom sheet. The trailing button is the
 * "Open full app" escape hatch: it flips the session override, which the
 * mobile gates observe reactively (`useFullAppOverride`), so the current
 * route re-renders as the desktop surface in place.
 */
export function MobileHomeHeader({
  workspaceId,
  workspaceName,
  workspaceIcon,
}: {
  /** Scoped workspace; all three unset on the unscoped home. */
  workspaceId?: string;
  workspaceName?: string;
  workspaceIcon?: MetadataIcon | null;
}) {
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const { data: userInfo } = useUserInfo();

  const firstName = userInfo?.user?.first_name?.trim();
  const greeting = firstName ? `Welcome, ${firstName}` : "Welcome back";

  const openFullApp = () => {
    setFullAppOverride(true);
    // The scoped gate re-renders in place via useFullAppOverride; the
    // unscoped home is `/` already, so this navigate is a no-op there.
    if (!workspaceId) navigate(ROUTES.HOME);
  };

  return (
    <div className="flex w-full items-center justify-between gap-4 select-none">
      <h1 className="min-w-0">
        <button
          type="button"
          aria-label="Switch workspace"
          data-state={switcherOpen ? "open" : "closed"}
          onClick={() => setSwitcherOpen(true)}
          className="group -ml-1 flex min-w-0 items-center gap-2.5 rounded-lg py-0.5 pl-1 pr-1.5 active:bg-muted/60 data-[state=open]:bg-muted/60"
        >
          {workspaceId ? (
            <WorkspaceRowIcon icon={workspaceIcon} />
          ) : (
            <LogoIcon className="h-[18px] w-[18px] shrink-0" />
          )}
          <span className="truncate text-xl font-medium text-n-90">
            {workspaceId ? (workspaceName ?? "Workspace") : greeting}
          </span>
          <SwitcherCaret />
        </button>
      </h1>

      <button
        type="button"
        aria-label="Open full app"
        title="Open full app"
        onClick={openFullApp}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground active:bg-accent"
      >
        <PanelsTopLeft className="h-[18px] w-[18px]" />
      </button>

      <MobileWorkspaceSwitcherSheet
        open={switcherOpen}
        onOpenChange={setSwitcherOpen}
        currentWorkspaceId={workspaceId}
      />
    </div>
  );
}

export default MobileHomeHeader;
